import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faBoxOpen, faReceipt } from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import SupervisorHistoryButton from '../common/SupervisorHistoryButton';
import AttachmentsPanel from '../common/AttachmentsPanel';
import { useTranslation } from 'react-i18next';
import CoilLineChips from '../coils/CoilLineChips';

const InfoRow = ({ label, value, isBig = false, tooltip = null }) => {
    if (value === null || value === undefined || value === '') return null;

    return (
        <div className="flex flex-col">
            <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: UI_COLORS.TEXT.MUTED }}>
                {label}
            </span>
            <span className={`font-medium mt-0.5 leading-tight ${isBig ? 'text-base' : 'text-[13px]'}`} style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                {value}
            </span>
            {tooltip && (
                <span className="text-[10px] mt-0.5 italic" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                    {tooltip}
                </span>
            )}
        </div>
    );
};


const DeliveryOrderViewModal = ({ isOpen, onClose, order, loading = false }) => {
    const { t } = useTranslation();
    if (!isOpen) return null;

    const strings = t('deliveryOrdersModule.view.fields', { returnObjects: true });
    const commonStrings = t('deliveryOrdersModule.common', { returnObjects: true });
    const emDash = commonStrings.symbols.emDash;

    const subOrderNumber = order?.sub_order_number || 'Delivery Order';
    const customerName = order?.customer_name || emDash;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}>
            <style>{`.orders-view-modal{ -ms-overflow-style: none; scrollbar-width: none; } .orders-view-modal::-webkit-scrollbar{ display: none; }`}</style>
            <div className="orders-view-modal w-full max-w-6xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-2xl" style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}>

                {/* Header */}
                <div className="flex items-center justify-between border-b px-6 py-5" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                    <div>
                        <p className="text-sm font-semibold uppercase tracking-wide" style={{ color: UI_COLORS.TEXT.MUTED }}>
                            {strings.headerTitle}
                        </p>
                        <h2 className="text-2xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                            {subOrderNumber}
                        </h2>
                        <p className="text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{customerName}</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="rounded-lg p-2 transition-all duration-200 hover:scale-105"
                        style={{
                            color: UI_COLORS.TEXT.MUTED,
                            backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.1),
                        }}
                    >
                        <FontAwesomeIcon icon={faTimes} className="text-lg" />
                    </button>
                </div>

                <div className="space-y-6 px-6 py-6">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-20">
                            <div className="h-10 w-10 animate-spin rounded-full border-4 border-b-transparent" style={{ borderColor: colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.5) }}></div>
                            <p className="mt-4 text-sm font-semibold" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{strings.loading}</p>
                        </div>
                    ) : !order ? (
                        <div className="rounded-xl border px-6 py-16 text-center" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                            <FontAwesomeIcon icon={faReceipt} className="text-4xl opacity-60" style={{ color: UI_COLORS.TEXT.MUTED }} />
                            <h3 className="mt-4 text-lg font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{strings.noData}</h3>
                        </div>
                    ) : (
                        <>
                            {/* Main Info Grid */}
                            <div className="grid grid-cols-2 gap-x-3 gap-y-3 md:grid-cols-4 p-3 md:p-4 rounded-xl border bg-gray-50/30 mb-6" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                                <InfoRow label={strings.orderNumber} value={order.order_number} />
                                <InfoRow label={strings.challanNumber} value={order.challan_number} />
                                <InfoRow label={strings.deliveryLocation} value={order.location_name} />
                                <InfoRow label={strings.totalWeight} value={order.total_weight ? `${order.total_weight} kg` : null} />

                                <InfoRow label={strings.challanDate} value={order.challan_date} />
                                <InfoRow label={t('common.formFields.labels.dueDate')} value={order.due_date} />
                                <SupervisorHistoryButton label={strings.supervisorName} supervisorName={order.supervisor_name} supervisorHistory={order.supervisor_history} />
                                <InfoRow label={strings.vehicleNumber} value={order.vehicle_number} />
                                <InfoRow label={strings.driverContact} value={order.driver_contact} />

                                <InfoRow label={strings.emptyTruckWeight} value={order.empty_truck_weight ? `${order.empty_truck_weight} kg` : null} />
                                <InfoRow label={strings.finalTruckWeight} value={order.final_truck_weight ? `${order.final_truck_weight} kg` : null} />
                                <InfoRow label={strings.calculatedTruckWeight} value={order.calculated_truck_weight ? `${order.calculated_truck_weight} kg` : null} />
                                
                                <InfoRow
                                    label={strings.remark}
                                    value={order.remark ? (
                                        <span className="block px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200" style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', whiteSpace: 'pre-wrap' }}>
                                            {order.remark}
                                        </span>
                                    ) : null}
                                />
                                <InfoRow
                                    label={t('common.formFields.labels.orderRemark')}
                                    value={(order.order_remark || order.remark) ? (
                                        <span className="block px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200" style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', whiteSpace: 'pre-wrap' }}>
                                            {order.order_remark || order.remark}
                                        </span>
                                    ) : null}
                                />

                                <InfoRow label={strings.vehicleRemark} value={order.vehicle_remark} />
                                <InfoRow
                                    label={t('common.formFields.labels.internalRemarks')}
                                    value={order.internal_remark ? (
                                        <span className="block px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800 border border-green-200" style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', whiteSpace: 'pre-wrap' }}>
                                            {order.internal_remark}
                                        </span>
                                    ) : null}
                                />
                                <InfoRow label={t('common.formFields.labels.assignedBy')} value={order.assigned_by_name} />
                                <InfoRow label={t('common.formFields.labels.deliveryOrderCreatedBy')} value={order.sdo_created_by_name} />
                                <InfoRow label={t('common.formFields.labels.orderCreatedBy')} value={order.do_created_by_name} />
                                <InfoRow label={t('common.formFields.labels.challanGeneratedBy')} value={order.challan_generated_by_name} />
                            </div>

                            {/* Items Table */}
                            <div className="mt-8">
                                <h3 className="text-sm font-semibold uppercase tracking-wide mb-4 flex items-center gap-2" style={{ color: UI_COLORS.TEXT.MUTED }}>
                                    <FontAwesomeIcon icon={faBoxOpen} className="text-xs" />
                                    {strings.items}
                                </h3>

                                <div className="rounded-xl border overflow-hidden" style={{ borderColor: UI_COLORS.BORDERS.LIGHT, backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}>
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-left border-collapse whitespace-nowrap">
                                            <thead>
                                                <tr className="border-b" style={{ borderColor: UI_COLORS.BORDERS.LIGHT, backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.HOVER, 0.1) }}>
                                                    <th className="px-4 py-4 text-center text-xs font-bold uppercase tracking-wider text-gray-500">#</th>
                                                    <th className="px-4 py-4 text-xs font-bold uppercase tracking-wider text-gray-500">{strings.item_product}</th>
                                                    <th className="px-4 py-4 text-center text-xs font-bold uppercase tracking-wider text-gray-500">{t('common.formFields.labels.pieces')}</th>
                                                    <th className="px-4 py-4 text-center text-xs font-bold uppercase tracking-wider text-gray-500">{strings.item_qty}</th>
                                                    <th className="px-4 py-4 text-center text-xs font-bold uppercase tracking-wider text-gray-500">{t('common.formFields.labels.price')}</th>
                                                    <th className="px-4 py-4 text-center text-xs font-bold uppercase tracking-wider text-gray-500">{strings.item_actual_quantity}</th>
                                                    <th className="px-4 py-4 text-center text-xs font-bold uppercase tracking-wider text-gray-500">{strings.item_remark}</th>
                                                 </tr>
                                             </thead>
                                             <tbody className="divide-y" style={{ divideColor: UI_COLORS.BORDERS.LIGHT }}>
                                                 {Array.isArray(order.items) && order.items.length > 0 ? (
                                                     order.items.map((it, idx) => (
                                                         <tr key={idx} className="hover:bg-gray-50/50 transition-colors">
                                                             <td className="px-4 py-4 text-center text-xs font-medium text-gray-400 align-top">
                                                                 {idx + 1}
                                                             </td>
                                                             <td className="px-4 py-4 align-top space-y-1">
                                                                 <p className="text-base font-bold text-wrap max-w-xs" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                                                                     {it.size_name || it.product_name || emDash}
                                                                 </p>
                                                                 <div className="flex flex-wrap gap-2 mt-1 items-center text-wrap max-w-xs">
                                                                     {it.category_name && <span className="text-xs font-medium text-gray-500">{it.category_name}</span>}
                                                                     {it.brand_name && <span className="text-xs font-medium text-gray-500">{it.brand_name}</span>}
                                                                 </div>
                                                               <CoilLineChips item={it} />
                                                             </td>
                                                             {/* Pieces */}
                                                             <td className="px-4 py-4 text-center align-top">
                                                                 {(() => {
                                                                     const pcs = (it.actual_pieces != null && Number(it.actual_pieces) !== 0)
                                                                         ? it.actual_pieces
                                                                         : (it.quantity_in_pieces != null && Number(it.quantity_in_pieces) !== 0)
                                                                         ? it.quantity_in_pieces
                                                                         : null;
                                                                     return pcs != null ? (
                                                                         <p className="text-lg font-bold text-blue-600">{pcs}</p>
                                                                     ) : <p className="text-sm text-gray-400">{emDash}</p>;
                                                                 })()}
                                                             </td>
                                                             {/* Order Quantity */}
                                                             <td className="px-4 py-4 text-center align-top">
                                                                 <p className="text-sm font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                                                                     {it.quantity ? `${it.quantity} ${it.unit || ''}` : emDash}
                                                                 </p>
                                                             </td>
                                                             {/* Price */}
                                                             <td className="px-4 py-4 text-center align-top">
                                                                 <p className="text-sm font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                                                                     {it.price != null ? `₹${it.price}` : emDash}
                                                                 </p>
                                                             </td>
                                                             {/* Actual Quantity */}
                                                             <td className="px-4 py-4 text-center align-top">
                                                                 <p className="text-sm font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                                                                     {(() => {
                                                                         const aq = it.actual_quantity;
                                                                         if (aq === null || aq === undefined || aq === '') return emDash;
                                                                         if (Number(aq) === 0) return emDash;
                                                                         return `${aq} ${it.unit || ''}`;
                                                                     })()}
                                                                 </p>
                                                             </td>
                                                             {/* Remark */}
                                                             <td className="px-4 py-4 text-center align-top min-w-[160px] max-w-[200px]">
                                                                 {it.remark ? (
                                                                     <span className="block px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200 text-left" style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', whiteSpace: 'pre-wrap' }}>
                                                                         {it.remark}
                                                                     </span>
                                                                 ) : emDash}
                                                             </td>
                                                         </tr>
                                                     ))
                                                 ) : (
                                                     <tr>
                                                         <td colSpan="7" className="px-4 py-12 text-center text-gray-400">
                                                            {strings.noItems}
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    {order && (
                        <AttachmentsPanel
                            subOrderId={order.id}
                            contextLabel="shared with all sub-orders"
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default DeliveryOrderViewModal;
